import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

// Pixels of scroll per frame — smaller on mobile so the sequence completes quickly
const SCROLL_PER_FRAME_DESKTOP = 15;
const SCROLL_PER_FRAME_MOBILE = 6;
const getScrollPerFrame = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    ? SCROLL_PER_FRAME_MOBILE
    : SCROLL_PER_FRAME_DESKTOP;

// Generate frame URLs
const generateFrameUrl = (baseUrl: string, index: number, delayPattern: string = '0.04s', padding: number = 3): string => {
  const paddedIndex = index.toString().padStart(padding, '0');
  return `${baseUrl}frame_${paddedIndex}_delay-${delayPattern}.webp`;
};

// Detect which delay pattern a frame sequence uses via Image loading (more reliable than HEAD)
const detectDelayPattern = (baseUrl: string): Promise<string> => {
  const patterns = ['0.04s', '0.041s', '0.029s', '0.042s'];
  
  return new Promise((resolve) => {
    let resolved = false;
    
    patterns.forEach((pattern) => {
      const img = new Image();
      const testUrl = generateFrameUrl(baseUrl, 0, pattern) + `?t=${Date.now()}`;
      
      img.onload = () => {
        if (!resolved) {
          resolved = true;
          resolve(pattern);
        }
      };
      
      img.onerror = () => {
        // If all patterns fail, the last one will resolve with default
      };
      
      img.src = testUrl;
    });
    
    // Timeout fallback after 3 seconds
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve('0.04s');
      }
    }, 3000);
  });
};

interface UseParallaxFramesReturn {
  currentFrame: number;
  loadingProgress: number;
  isLoaded: boolean;
  frames: HTMLImageElement[];
  framesComplete: boolean;
  slideProgress: number;
  totalScrollHeight: number;
  resetFrames: () => void;
}

export const useParallaxFrames = (
  frameBaseUrl: string,
  totalFrames: number = 240,
  isActive: boolean = true,
  explicitDelayPattern?: '0.04s' | '0.041s' | '0.029s' | '0.042s',
  framePadding: number = 3
): UseParallaxFramesReturn => {
  const [frames, setFrames] = useState<HTMLImageElement[]>([]);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [framesComplete, setFramesComplete] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 1000);
  
  const loadedCount = useRef(0);
  const loadRequestId = useRef(0);

  const [scrollPerFrame, setScrollPerFrame] = useState(getScrollPerFrame);
  // Dynamic frame scroll height based on total frames
  const frameScrollHeight = totalFrames * scrollPerFrame;

  // Track window height + scroll-per-frame for dynamic responsiveness
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      setScrollPerFrame(getScrollPerFrame());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Spacer reserves scroll room for the frame sequence; content flows right after
  // (no extra slide-up scroll — content naturally rises into view as you scroll past).
  const slideUpScrollHeight = 0;
  const totalScrollHeight = frameScrollHeight + windowHeight;

  // Reset state synchronously before paint when URL changes
  useLayoutEffect(() => {
    loadRequestId.current += 1;
    setFrames([]);
    setLoadingProgress(0);
    setIsLoaded(false);
    setCurrentFrame(0);
    setFramesComplete(false);
    setSlideProgress(0);
    loadedCount.current = 0;
  }, [frameBaseUrl, totalFrames]);

  // Preload all frames
  useEffect(() => {
    const currentRequestId = loadRequestId.current;

    const loadFrames = async () => {
      // Use explicit pattern if provided, otherwise detect
      const delayPattern = explicitDelayPattern || await detectDelayPattern(frameBaseUrl);
      
      // Check if this load was cancelled
      if (currentRequestId !== loadRequestId.current) return;
      
      const imagePromises: Promise<HTMLImageElement | null>[] = [];
      const loadedFrames: HTMLImageElement[] = new Array(totalFrames);

      // Reveal site as soon as we have a small batch of valid frames
      // (avoids appearing "stuck" on mobile while remaining frames stream in)
      const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
      const earlyRevealCount = isMobile ? Math.min(8, totalFrames) : Math.min(24, totalFrames);
      let revealed = false;

      const tryEarlyReveal = () => {
        if (revealed) return;
        if (currentRequestId !== loadRequestId.current) return;
        let validInRow = 0;
        for (let i = 0; i < earlyRevealCount; i++) {
          if (loadedFrames[i]) validInRow++;
        }
        if (validInRow >= earlyRevealCount) {
          revealed = true;
          // Backfill any later empty slots with the latest valid frame for now;
          // they'll be replaced as real frames finish decoding.
          const firstValid = loadedFrames.find(f => f) || null;
          if (firstValid) {
            const filled = loadedFrames.slice();
            for (let i = 0; i < totalFrames; i++) {
              if (!filled[i]) filled[i] = firstValid;
            }
            setFrames(filled);
            setIsLoaded(true);
          }
        }
      };

      for (let i = 0; i < totalFrames; i++) {
        const promise = new Promise<HTMLImageElement | null>((resolve) => {
          const img = new Image();
          img.onload = async () => {
            try {
              await img.decode();
              loadedFrames[i] = img;
            } catch (e) {
              console.warn(`Frame ${i} failed to decode from ${frameBaseUrl}`);
            }
            loadedCount.current++;
            if (currentRequestId === loadRequestId.current) {
              setLoadingProgress((loadedCount.current / totalFrames) * 100);
              tryEarlyReveal();
            }
            resolve(loadedFrames[i] || null);
          };
          img.onerror = () => {
            console.warn(`Frame ${i} failed to load from ${frameBaseUrl}`);
            loadedCount.current++;
            if (currentRequestId === loadRequestId.current) {
              setLoadingProgress((loadedCount.current / totalFrames) * 100);
              tryEarlyReveal();
            }
            resolve(null);
          };
          img.src = generateFrameUrl(frameBaseUrl, i, delayPattern, framePadding);
        });
        imagePromises.push(promise);
      }

      const results = await Promise.all(imagePromises);
      
      // Check if this load was cancelled
      if (currentRequestId !== loadRequestId.current) return;
      
      // Fill in gaps with the nearest valid frame
      let lastValidFrame: HTMLImageElement | null = null;
      
      for (let i = 0; i < results.length; i++) {
        if (results[i]) {
          loadedFrames[i] = results[i]!;
          lastValidFrame = results[i];
        } else if (lastValidFrame) {
          loadedFrames[i] = lastValidFrame;
        }
      }
      
      // If first frames failed, fill with the first valid frame found
      const firstValid = loadedFrames.find(f => f);
      if (firstValid) {
        for (let i = 0; i < loadedFrames.length; i++) {
          if (!loadedFrames[i]) {
            loadedFrames[i] = firstValid;
          }
        }
      }
      
      // Only mark as loaded if at least one frame succeeded
      const hasValidFrames = loadedFrames.some(f => f);
      
      if (hasValidFrames) {
        setFrames(loadedFrames);
        setIsLoaded(true);
      } else {
        console.error(`No frames loaded from ${frameBaseUrl}. Check the URL and delay pattern.`);
      }
    };

      loadFrames();
  }, [frameBaseUrl, totalFrames, explicitDelayPattern, framePadding]);

  // Handle scroll position to drive frame playback
  useEffect(() => {
    if (!isLoaded || !isActive) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      // Calculate frame based on scroll position
      const frameIndex = Math.floor(scrollY / scrollPerFrame);
      const clampedFrame = Math.max(0, Math.min(frameIndex, totalFrames - 1));
      
      setCurrentFrame(clampedFrame);
      
      // Check if frames are complete
      const isComplete = clampedFrame >= totalFrames - 1;
      setFramesComplete(isComplete);

      // Slide progress is now driven simply by whether frames are complete
      setSlideProgress(isComplete ? 1 : 0);
    };

    // Initial calculation
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoaded, isActive, totalFrames, frameScrollHeight, slideUpScrollHeight, scrollPerFrame]);

  const resetFrames = useCallback(() => {
    setCurrentFrame(0);
    setFramesComplete(false);
    setSlideProgress(0);
  }, []);

  return {
    currentFrame,
    loadingProgress,
    isLoaded,
    frames,
    framesComplete,
    slideProgress,
    totalScrollHeight,
    resetFrames,
  };
};

export { generateFrameUrl };
